import { useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { z } from 'zod';
import {
  AlertCircle,
  ArrowRight,
  BarChart3,
  Eye,
  EyeOff,
  FileText,
  Loader2,
  Lock,
  Mail,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { ErrorDeApi } from '@/shared/api/problema';
import { useSesion } from '@/shared/sesion/SesionProvider';

const MESA_AYUDA = {
  telefono: '(03) 281-0164',
  telefonoEnlace: 'tel:+59332810164',
  sitio: 'epagal.gob.ec',
} as const;

const LO_QUE_HACE = [
  {
    icono: FileText,
    titulo: 'Recaudación segura',
    detalle: 'Procesa y gestiona tus ingresos con total confianza.',
  },
  {
    icono: Users,
    titulo: 'Información centralizada',
    detalle: 'Contribuyentes, cartera y reportes en una sola plataforma.',
  },
  {
    icono: BarChart3,
    titulo: 'Decisiones con datos',
    detalle: 'Información confiable para planificar el servicio de aseo y gestión ambiental.',
  },
] as const;

const esquema = z.object({
  correo: z.string().min(1, 'Ingrese su correo').email('El correo no tiene un formato válido'),
  clave: z.string().min(1, 'Ingrese su contraseña'),
});

type Credenciales = z.infer<typeof esquema>;

export function PaginaAcceso() {
  const { perfil, cargando, acceder } = useSesion();
  const navegar = useNavigate();
  const ubicacion = useLocation();

  const [verClave, setVerClave] = useState(false);
  const [verAyudaDeClave, setVerAyudaDeClave] = useState(false);
  const [errorGeneral, setErrorGeneral] = useState<ErrorDeApi | Error | null>(null);

  const idCorreo = useId();
  const idClave = useId();
  const idAyudaDeClave = useId();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Credenciales>({
    resolver: zodResolver(esquema),
    defaultValues: { correo: '', clave: '' },
  });

  if (cargando) {
    return null;
  }
  if (perfil) {
    return <Navigate to="/inicio" replace />;
  }

  const enviar = handleSubmit(async ({ correo, clave }) => {
    setErrorGeneral(null);
    try {
      await acceder(correo, clave);
      const destino = (ubicacion.state as { destino?: string } | null)?.destino ?? '/inicio';
      navegar(destino, { replace: true });
    } catch (causa) {
      setErrorGeneral(
        causa instanceof ErrorDeApi
          ? causa
          : new Error('NO SE PUDO CONTACTAR CON EL SERVIDOR. INTENTE DE NUEVO.'),
      );
    }
  });

  const bloqueado = errorGeneral instanceof ErrorDeApi && errorGeneral.status === 429;

  const claseCampo = (invalido: boolean) =>
    [
      'flex h-[50px] items-center gap-2.5 rounded-xl border bg-white px-3.5 transition-colors',
      'focus-within:ring-4 focus-within:ring-acceso-500/15',
      invalido
        ? 'border-alerta focus-within:border-alerta'
        : 'border-armazon-200 focus-within:border-acceso-500',
    ].join(' ');

  return (

    <div className="relative flex h-dvh flex-col overflow-hidden bg-armazon-900">

      <img
        src="/fondo-acceso.webp"
        alt=""
        aria-hidden
        className="absolute inset-0 size-full object-cover object-center"
      />

      <div className="relative z-10 flex h-full min-h-0 flex-col">
        <header className="flex shrink-0 items-start justify-between gap-4 px-6 pt-6 sm:px-10 sm:pt-8">
          <div className="flex items-center gap-3">
            <img
              src="/isotipo-epagal.png"
              alt=""
              aria-hidden
              width={128}
              height={128}
              className="size-11 shrink-0 sm:size-12"
            />
            <div className="min-w-0">
              <p className="text-2xl font-extrabold leading-none tracking-tight text-white sm:text-[28px]">
                SIREPAGAL
              </p>
              <p className="mt-1 text-sm font-medium tracking-wide text-armazon-200">EPAGAL EP</p>
            </div>
          </div>

          <div className="hidden text-right lg:block">
            <p className="text-[11px] font-semibold uppercase leading-relaxed tracking-[0.12em] text-armazon-700">
              Empresa Pública de Aseo y Gestión
              <br />
              Ambiental del cantón Latacunga
            </p>
            <span className="mt-2 ml-auto block h-0.5 w-24 rounded-full bg-marca-500" />
          </div>
        </header>

        <div className="grid min-h-0 flex-1 gap-10 px-6 py-6 sm:px-10 lg:grid-cols-[1.1fr_minmax(0,470px)] lg:gap-14">

          <section className="hidden self-center overflow-y-auto text-white lg:block">
            <h1 className="text-5xl font-extrabold leading-[1.08] tracking-tight xl:text-[56px]">
              Gestión inteligente
              <br />
              de la <span className="text-marca-400">recaudación</span>
            </h1>
            <p className="mt-5 max-w-[31rem] text-balance text-lg leading-relaxed text-armazon-100">
              Una sola plataforma para gestionar pagos, cartera, facturación, contribuyentes y
              reportes.
            </p>

            <ul className="mt-10 grid gap-6">
              {LO_QUE_HACE.map(({ icono: Icono, titulo, detalle }) => (
                <li key={titulo} className="flex items-start gap-4">
                  <span className="grid size-12 shrink-0 place-items-center rounded-full bg-white/10 text-white ring-1 ring-white/20">
                    <Icono className="size-5" aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <p className="text-base font-bold">{titulo}</p>
                    <p className="mt-0.5 max-w-[22rem] text-pretty text-sm leading-relaxed text-armazon-200">
                      {detalle}
                    </p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-12">
              <span className="block h-0.5 w-14 rounded-full bg-marca-400" />
              <p className="mt-3 text-xs font-semibold uppercase leading-relaxed tracking-[0.18em] text-white/70">
                Trabajando por una
                <br />
                Latacunga más limpia
              </p>
            </div>
          </section>

          <main className="grid min-h-0 place-items-center overflow-y-auto">
            <div className="w-full max-w-[470px] rounded-3xl bg-white p-7 shadow-2xl shadow-armazon-950/25 sm:p-8">
              <img
                src="/marca-epagal.webp"
                alt="EPAGAL"
                width={121}
                height={32}
                className="mx-auto h-9 w-auto"
              />
              <p className="mt-1 text-center text-base font-semibold tracking-tight text-armazon-600">
                SIREPAGAL
              </p>

              <h2 className="mt-5 text-center text-[28px] font-extrabold leading-tight tracking-tight text-armazon-900">
                Iniciar sesión
              </h2>
              <p className="mx-auto mt-1.5 mb-6 max-w-[22rem] text-center text-sm leading-relaxed text-armazon-500">
                Accede al sistema de recaudación de EPAGAL EP de forma segura.
              </p>

              {errorGeneral && (
                <div
                  role="alert"
                  className={[
                    'mb-5 flex items-start gap-2.5 rounded-xl border p-3 text-sm',
                    bloqueado
                      ? 'border-amber-200 bg-amber-50 text-amber-900'
                      : 'border-red-200 bg-red-50 text-red-900',
                  ].join(' ')}
                >
                  <AlertCircle className="mt-px size-[18px] shrink-0" aria-hidden />
                  <span>
                    {errorGeneral.message}

                    {errorGeneral instanceof ErrorDeApi &&
                      errorGeneral.status >= 500 &&
                      errorGeneral.problema.incidente && (
                        <span className="mt-1 block text-xs opacity-80">
                          Referencia: {errorGeneral.problema.incidente}
                        </span>
                      )}
                  </span>
                </div>
              )}

              <form onSubmit={enviar} noValidate>
                <div>

                  <label
                    className="mb-1.5 block text-sm font-semibold text-armazon-800"
                    htmlFor={idCorreo}
                  >
                    Correo electrónico
                  </label>
                  <div className={claseCampo(Boolean(errors.correo))}>
                    <Mail className="size-[18px] shrink-0 text-armazon-400" aria-hidden />
                    <input
                      id={idCorreo}
                      type="email"
                      autoComplete="username"
                      autoFocus
                      placeholder="usuario@epagal.gob.ec"
                      aria-invalid={errors.correo ? true : undefined}
                      aria-describedby={errors.correo ? `${idCorreo}-error` : undefined}
                      {...register('correo')}
                      className="min-w-0 flex-1 border-0 bg-transparent text-sm font-medium text-armazon-900 outline-none placeholder:font-normal placeholder:text-armazon-400"
                    />
                  </div>
                  {errors.correo && (
                    <span id={`${idCorreo}-error`} className="mt-1.5 block text-xs text-alerta">
                      {errors.correo.message}
                    </span>
                  )}
                </div>

                <div className="mt-4">
                  <label
                    className="mb-1.5 block text-sm font-semibold text-armazon-800"
                    htmlFor={idClave}
                  >
                    Contraseña
                  </label>
                  <div className={claseCampo(Boolean(errors.clave))}>
                    <Lock className="size-[18px] shrink-0 text-armazon-400" aria-hidden />
                    <input
                      id={idClave}
                      type={verClave ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="Ingresa tu contraseña"
                      aria-invalid={errors.clave ? true : undefined}
                      aria-describedby={errors.clave ? `${idClave}-error` : undefined}
                      {...register('clave')}
                      className="min-w-0 flex-1 border-0 bg-transparent text-sm font-medium text-armazon-900 outline-none placeholder:font-normal placeholder:text-armazon-400"
                    />
                    <button
                      type="button"
                      onClick={() => setVerClave((visible) => !visible)}
                      aria-label={verClave ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                      aria-pressed={verClave}
                      className="grid size-8 shrink-0 place-items-center rounded-md text-armazon-400 transition-colors hover:bg-armazon-100 hover:text-armazon-700"
                    >
                      {verClave ? (
                        <EyeOff className="size-[18px]" aria-hidden />
                      ) : (
                        <Eye className="size-[18px]" aria-hidden />
                      )}
                    </button>
                  </div>
                  {errors.clave && (
                    <span id={`${idClave}-error`} className="mt-1.5 block text-xs text-alerta">
                      {errors.clave.message}
                    </span>
                  )}
                </div>

                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setVerAyudaDeClave((visible) => !visible)}
                    aria-expanded={verAyudaDeClave}
                    aria-controls={idAyudaDeClave}
                    className="text-sm font-medium text-acceso-600 transition-colors hover:text-acceso-700 hover:underline"
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>
                {verAyudaDeClave && (
                  <p
                    id={idAyudaDeClave}
                    className="mt-2 rounded-xl bg-armazon-50 p-3 text-xs leading-relaxed text-armazon-600 ring-1 ring-armazon-200"
                  >
                    La restablece un administrador desde usuarios. Si no, la mesa de ayuda:{' '}
                    <a
                      href={MESA_AYUDA.telefonoEnlace}
                      className="font-semibold text-acceso-600 hover:underline"
                    >
                      {MESA_AYUDA.telefono}
                    </a>
                    .
                  </p>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="mt-5 flex h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-acceso-600 text-base font-semibold text-white transition-colors hover:bg-acceso-700 active:translate-y-px disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="size-5 animate-spin" aria-hidden />
                      Verificando…
                    </>
                  ) : (
                    <>
                      Ingresar
                      <ArrowRight className="size-5" aria-hidden />
                    </>
                  )}
                </button>
              </form>

              <p className="mt-6 flex items-center justify-center gap-2 border-t border-armazon-200 pt-4 text-sm text-armazon-500">
                <ShieldCheck className="size-[18px] shrink-0 text-armazon-400" aria-hidden />
                Acceso seguro para personal autorizado
              </p>
            </div>
          </main>
        </div>

        <footer className="flex shrink-0 flex-col items-center justify-between gap-2 border-t border-white/10 bg-armazon-950/60 px-6 py-3 text-xs text-armazon-200 backdrop-blur-sm sm:flex-row sm:px-10">
          <p>
            © {new Date().getFullYear()} SIREPAGAL — EPAGAL EP
            <span className="mx-2 text-white/25">|</span>
            Recaudación de aseo y gestión ambiental
          </p>
          <p className="flex items-center gap-2">
            <a
              href={MESA_AYUDA.telefonoEnlace}
              className="transition-colors hover:text-white hover:underline"
            >
              Soporte técnico {MESA_AYUDA.telefono}
            </a>
            <span className="text-white/25">|</span>
            <a
              href={`https://${MESA_AYUDA.sitio}`}
              target="_blank"
              rel="noreferrer noopener"
              className="transition-colors hover:text-white hover:underline"
            >
              {MESA_AYUDA.sitio}
            </a>
          </p>
        </footer>
      </div>
    </div>
  );
}
